
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Layout, Card, Row, Col, Space, Table, Descriptions, Avatar, Tag,
    Tabs, Button, Statistic, List, Timeline, Input, message, Form, Select
} from 'antd';
import {
    UserOutlined, ArrowLeftOutlined, RobotOutlined,
    SendOutlined, BarChartOutlined
} from '@ant-design/icons';
import { Influencer, Collaboration, InteractionLog, marketingAPI } from '../../services/marketingAPI';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { TextArea } = Input;

const InfluencerDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [influencer, setInfluencer] = useState<Influencer | null>(null);
    const [interactions, setInteractions] = useState<InteractionLog[]>([]);
    const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
    const [loading, setLoading] = useState(false);

    // Interaction Form
    const [logForm] = Form.useForm();

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const infRes = await marketingAPI.getInfluencer(Number(id));
            setInfluencer(infRes.data);

            const intRes = await marketingAPI.listInteractions({ influencer: id });
            // Handle both array and paginated response
            setInteractions(Array.isArray(intRes.data) ? intRes.data : (intRes.data as any).results || []);

            const colRes = await marketingAPI.getCollaborationsByInfluencer(Number(id));
            // Handle both array and paginated response
            setCollaborations(Array.isArray(colRes.data) ? colRes.data : (colRes.data as any).results || []);
        } catch (error) {
            message.error('加载详情失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchDetail();
    }, [id]);

    const handleAddLog = async (values: any) => {
        try {
            await marketingAPI.createInteraction({
                ...values,
                influencer: Number(id)
            });
            message.success('记录添加成功');
            logForm.resetFields();
            fetchDetail(); // Refresh
        } catch (error) {
            message.error('添加失败');
        }
    };

    const triggerAnalysis = async () => {
        try {
            message.loading({ content: 'AI 分析中...', key: 'analyzing' });
            await marketingAPI.analyzeInfluencer(Number(id));
            message.success({ content: '分析任务已提交', key: 'analyzing' });
        } catch (error) {
            message.error({ content: '提交分析失败', key: 'analyzing' });
        }
    };

    if (!influencer) return <div>Loading...</div>;

    return (
        <div style={{ padding: '24px' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
                返回列表
            </Button>

            <Row gutter={24}>
                {/* Left Column: Profile & Stats */}
                <Col span={16}>
                    <Card style={{ marginBottom: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar size={80} src={influencer.avatar} icon={<UserOutlined />} />
                            <div style={{ marginLeft: 24, flex: 1 }}>
                                <h1>{influencer.name} <Tag color="blue">{influencer.status.toUpperCase()}</Tag></h1>
                                <p>{influencer.email}</p>
                                <Space>
                                    {influencer.tags.map(t => <Tag key={t}>{t}</Tag>)}
                                </Space>
                            </div>
                            <Button type="primary" icon={<RobotOutlined />} onClick={triggerAnalysis}>
                                AI 深度分析
                            </Button>
                        </div>
                    </Card>

                    <Card title="AI 分析报告" style={{ marginBottom: 24 }}>
                        {influencer.latest_analysis ? (
                            <div>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic title="品牌契合度" value={influencer.latest_analysis.brand_fit_score} suffix="/ 100" />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic title="商业价值" value={influencer.latest_analysis.commercial_value} />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic title="增长潜力" value={influencer.latest_analysis.growth_potential} />
                                    </Col>
                                </Row>
                                <div style={{ marginTop: 16 }}>
                                    <h3>受众画像</h3>
                                    <pre style={{ background: '#f5f5f5', padding: 8 }}>
                                        {JSON.stringify(influencer.latest_analysis.audience_persona, null, 2)}
                                    </pre>
                                    <h3>总结</h3>
                                    <p>{influencer.latest_analysis.summary}</p>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>暂无分析报告，请点击右上角进行 AI 分析</div>
                        )}
                    </Card>

                    <Card title="合作历史 (ROI)">
                        <Table
                            dataSource={collaborations}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                { title: '活动', dataIndex: 'campaign_name' },
                                { title: '状态', dataIndex: 'status_display' },
                                { title: '总花费', dataIndex: 'total_cost', render: (val: number) => `$${val}` },
                                { title: '销售额', dataIndex: 'result_sales', render: (val: number) => `$${val}` },
                                { title: 'ROI', dataIndex: 'roi', render: (val: number) => val ? val.toFixed(2) : '0.00' },
                            ]}
                        />
                    </Card>
                </Col>

                {/* Right Column: CRM & Info */}
                <Col span={8}>
                    <Card title="跟进记录 (CRM)" style={{ height: '100%' }}>
                        <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16 }}>
                            <Timeline>
                                {interactions.map(log => (
                                    <Timeline.Item key={log.id} color="blue">
                                        <p style={{ marginBottom: 4 }}>
                                            <span style={{ fontWeight: 'bold' }}>{log.type_display}</span> - {dayjs(log.date).format('MM-DD HH:mm')}
                                            <span style={{ float: 'right', color: '#999' }}>{log.user_name}</span>
                                        </p>
                                        <div style={{ color: '#666' }}>{log.content}</div>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        </div>
                        <Form form={logForm} onFinish={handleAddLog}>
                            <Form.Item name="type" initialValue="email">
                                <Select>
                                    <Select.Option value="email">邮件</Select.Option>
                                    <Select.Option value="dm">私信</Select.Option>
                                    <Select.Option value="sample">寄样</Select.Option>
                                    <Select.Option value="other">其他</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="content" rules={[{ required: true, message: '请输入内容' }]}>
                                <TextArea rows={3} placeholder="记录沟通内容..." />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined />} block>
                                添加记录
                            </Button>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default InfluencerDetail;
